import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Plus, Trash2 } from "lucide-react";
import { adminCreateRecipe, adminUpdateRecipe, adminUpload, fileUrl } from "@/lib/api";

const CATEGORIES = ["Chicken", "Seafood", "Snacks", "Veggies", "Desserts"];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const emptyRecipe = {
  title: "",
  slug: "",
  description: "",
  long_description: "",
  image_url: "",
  category: "Chicken",
  cook_time_minutes: 10,
  prep_time_minutes: 5,
  total_time_minutes: 15,
  servings: 4,
  calories: 300,
  difficulty: "Easy",
  ingredients: [{ amount: "", name: "" }],
  steps: [{ number: 1, instruction: "", tip: "" }],
  pro_tips: [""],
  featured: false,
  new_this_week: false,
  recommended_product: null,
};

export default function RecipeForm({ recipe, onCancel, onSaved }) {
  const isNew = !recipe;
  const [form, setForm] = useState(() => recipe ? { ...emptyRecipe, ...recipe, ingredients: recipe.ingredients?.length ? recipe.ingredients : [{ amount: "", name: "" }], steps: recipe.steps?.length ? recipe.steps : [{ number: 1, instruction: "", tip: "" }], pro_tips: recipe.pro_tips?.length ? recipe.pro_tips : [""] } : emptyRecipe);
  const [includeProduct, setIncludeProduct] = useState(!!recipe?.recommended_product);
  const [product, setProduct] = useState(recipe?.recommended_product || { name: "", description: "", image_url: "", amazon_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = isNew ? "New recipe — JustAirFry" : `Edit ${form.title} — JustAirFry`;
  }, [isNew, form.title]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onTitleChange = (v) => {
    setForm((f) => ({
      ...f,
      title: v,
      slug: isNew && (!f.slug || f.slug === slugify(f.title)) ? slugify(v) : f.slug,
    }));
  };

  const onImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload(file);
      set("image_url", fileUrl(res.url));
      toast.success("Image uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload(file);
      setProduct((p) => ({ ...p, image_url: fileUrl(res.url) }));
      toast.success("Product image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Array helpers
  const updateIngredient = (i, patch) =>
    setForm((f) => {
      const arr = [...f.ingredients];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, ingredients: arr };
    });
  const addIngredient = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { amount: "", name: "" }] }));
  const removeIngredient = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const updateStep = (i, patch) =>
    setForm((f) => {
      const arr = [...f.steps];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, steps: arr };
    });
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { number: f.steps.length + 1, instruction: "", tip: "" }] }));
  const removeStep = (i) =>
    setForm((f) => {
      const arr = f.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, number: idx + 1 }));
      return { ...f, steps: arr };
    });

  const updateTip = (i, v) =>
    setForm((f) => {
      const arr = [...f.pro_tips];
      arr[i] = v;
      return { ...f, pro_tips: arr };
    });
  const addTip = () => setForm((f) => ({ ...f, pro_tips: [...f.pro_tips, ""] }));
  const removeTip = (i) => setForm((f) => ({ ...f, pro_tips: f.pro_tips.filter((_, idx) => idx !== i) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim() || !form.description.trim() || !form.image_url) {
      toast.error("Title, slug, description and hero image are required.");
      return;
    }
    const payload = {
      ...form,
      slug: slugify(form.slug),
      ingredients: form.ingredients.filter((i) => i.name.trim()),
      steps: form.steps
        .filter((s) => s.instruction.trim())
        .map((s, idx) => ({ number: idx + 1, instruction: s.instruction, tip: s.tip || null })),
      pro_tips: form.pro_tips.filter((t) => t.trim()),
      recommended_product: includeProduct && product.name.trim() ? product : null,
    };

    setSaving(true);
    try {
      if (isNew) {
        await adminCreateRecipe(payload);
        toast.success("Recipe published!");
      } else {
        await adminUpdateRecipe(recipe.slug, payload);
        toast.success("Recipe updated!");
      }
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full h-11 px-3.5 rounded-md bg-white text-sm outline-none focus:ring-2";
  const inputStyle = { border: "1px solid var(--jaf-border)", color: "var(--jaf-text)" };
  const textareaCls = "w-full px-3.5 py-3 rounded-md bg-white text-sm outline-none focus:ring-2";
  const label = "block text-xs font-medium uppercase tracking-wider mb-1.5";

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--jaf-bg)" }} data-testid="recipe-form">
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{ backgroundColor: "rgba(253,251,247,0.92)", borderBottom: "1px solid var(--jaf-border)" }}
      >
        <div className="jaf-container h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={onCancel}
            data-testid="back-to-admin"
            className="inline-flex items-center gap-1.5 text-sm"
            style={{ color: "var(--jaf-text-muted)" }}
          >
            <ArrowLeft size={16} /> Back to dashboard
          </button>
          <h1 className="font-serif text-xl">{isNew ? "New recipe" : "Edit recipe"}</h1>
          <button
            type="submit"
            form="recipe-form-el"
            disabled={saving}
            data-testid="save-recipe-btn"
            className="jaf-btn-primary px-5 h-10 rounded-md text-sm font-medium disabled:opacity-60"
          >
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </header>

      <form id="recipe-form-el" onSubmit={onSubmit} className="jaf-container py-10 grid lg:grid-cols-3 gap-8">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-7">
          {/* Title + slug + description */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <h2 className="font-serif text-xl mb-4">Recipe basics</h2>
            <label className={label}>Title</label>
            <input type="text" value={form.title} onChange={(e) => onTitleChange(e.target.value)} data-testid="form-title" required className={inputCls} style={inputStyle} placeholder="Crispy Chicken Wings" />

            <div className="mt-4">
              <label className={label}>URL slug</label>
              <input type="text" value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="form-slug" required className={inputCls} style={inputStyle} placeholder="crispy-chicken-wings" />
            </div>

            <div className="mt-4">
              <label className={label}>Short description (shown in cards)</label>
              <textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="form-description" required className={textareaCls} style={inputStyle} />
            </div>

            <div className="mt-4">
              <label className={label}>Long description (recipe page intro)</label>
              <textarea rows={4} value={form.long_description || ""} onChange={(e) => set("long_description", e.target.value)} data-testid="form-long-description" className={textareaCls} style={inputStyle} />
            </div>
          </section>

          {/* Ingredients */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }} data-testid="form-ingredients-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Ingredients</h2>
              <button type="button" onClick={addIngredient} data-testid="add-ingredient" className="text-sm inline-flex items-center gap-1" style={{ color: "var(--jaf-primary)" }}>
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="space-y-3">
              {form.ingredients.map((ing, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input type="text" placeholder="2 tbsp" value={ing.amount} onChange={(e) => updateIngredient(i, { amount: e.target.value })} data-testid={`ing-amount-${i}`} className="w-32 h-10 px-3 rounded-md bg-white text-sm outline-none" style={inputStyle} />
                  <input type="text" placeholder="Olive oil" value={ing.name} onChange={(e) => updateIngredient(i, { name: e.target.value })} data-testid={`ing-name-${i}`} className="flex-1 h-10 px-3 rounded-md bg-white text-sm outline-none" style={inputStyle} />
                  <button type="button" onClick={() => removeIngredient(i)} className="p-2 hover:bg-red-50 rounded-md" style={{ color: "#b04444" }} aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Steps */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }} data-testid="form-steps-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Step-by-step instructions</h2>
              <button type="button" onClick={addStep} data-testid="add-step" className="text-sm inline-flex items-center gap-1" style={{ color: "var(--jaf-primary)" }}>
                <Plus size={14} /> Add step
              </button>
            </div>
            <div className="space-y-5">
              {form.steps.map((s, i) => (
                <div key={i} className="flex gap-3">
                  <span className="recipe-step-num flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 space-y-2">
                    <textarea rows={2} value={s.instruction} onChange={(e) => updateStep(i, { instruction: e.target.value })} data-testid={`step-${i}`} placeholder="Pat the wings dry…" className={textareaCls} style={inputStyle} />
                    <input type="text" value={s.tip || ""} onChange={(e) => updateStep(i, { tip: e.target.value })} placeholder="Optional tip — appears in a callout box" data-testid={`step-tip-${i}`} className={inputCls} style={inputStyle} />
                  </div>
                  <button type="button" onClick={() => removeStep(i)} className="p-2 hover:bg-red-50 rounded-md self-start" style={{ color: "#b04444" }} aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Pro tips */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Pro tips</h2>
              <button type="button" onClick={addTip} data-testid="add-tip" className="text-sm inline-flex items-center gap-1" style={{ color: "var(--jaf-primary)" }}>
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="space-y-3">
              {form.pro_tips.map((t, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={t} onChange={(e) => updateTip(i, e.target.value)} data-testid={`tip-${i}`} placeholder="Pat wings completely dry for max crispness" className={`flex-1 ${inputCls}`} style={inputStyle} />
                  <button type="button" onClick={() => removeTip(i)} className="p-2 hover:bg-red-50 rounded-md" style={{ color: "#b04444" }} aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Recommended product */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeProduct}
                onChange={(e) => setIncludeProduct(e.target.checked)}
                data-testid="include-product"
                className="w-5 h-5 accent-[color:var(--jaf-primary)]"
              />
              <span className="font-serif text-xl">Include recommended product (Amazon affiliate)</span>
            </label>
            {includeProduct && (
              <div className="mt-5 space-y-4">
                <div>
                  <label className={label}>Product name</label>
                  <input type="text" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} data-testid="product-name" className={inputCls} style={inputStyle} />
                </div>
                <div>
                  <label className={label}>Description</label>
                  <textarea rows={2} value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} data-testid="product-description" className={textareaCls} style={inputStyle} />
                </div>
                <div>
                  <label className={label}>Amazon URL</label>
                  <input type="url" value={product.amazon_url} onChange={(e) => setProduct({ ...product, amazon_url: e.target.value })} data-testid="product-url" className={inputCls} style={inputStyle} placeholder="https://amazon.com/dp/..." />
                </div>
                <div>
                  <label className={label}>Product image</label>
                  <div className="flex items-center gap-3">
                    {product.image_url && <img src={product.image_url} alt="" className="h-16 w-16 rounded-md object-cover" />}
                    <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm cursor-pointer" style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }}>
                      <ImagePlus size={14} /> {product.image_url ? "Change" : "Upload"}
                      <input type="file" accept="image/*" onChange={onProductImageUpload} className="hidden" data-testid="product-image-upload" />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-7">
          {/* Hero image */}
          <section className="rounded-xl p-6" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <h2 className="font-serif text-xl mb-4">Hero image</h2>
            {form.image_url ? (
              <img src={form.image_url} alt="" className="w-full aspect-[4/3] rounded-md object-cover mb-3" data-testid="hero-preview" />
            ) : (
              <div className="w-full aspect-[4/3] rounded-md mb-3 flex items-center justify-center text-sm" style={{ backgroundColor: "var(--jaf-surface)", color: "var(--jaf-text-muted)" }}>
                No image yet
              </div>
            )}
            <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm cursor-pointer w-full justify-center" style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }}>
              <ImagePlus size={14} /> {uploading ? "Uploading…" : form.image_url ? "Replace image" : "Upload image"}
              <input type="file" accept="image/*" onChange={onImageUpload} className="hidden" data-testid="hero-image-upload" disabled={uploading} />
            </label>
            <div className="mt-3">
              <label className={label}>…or paste an image URL</label>
              <input type="url" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." data-testid="form-image-url" className={inputCls} style={inputStyle} />
            </div>
          </section>

          {/* Meta */}
          <section className="rounded-xl p-6 space-y-4" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <h2 className="font-serif text-xl">Details</h2>
            <div>
              <label className={label}>Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="form-category" className={inputCls} style={inputStyle}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Difficulty</label>
              <select value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} data-testid="form-difficulty" className={inputCls} style={inputStyle}>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Prep (min)</label>
                <input type="number" min={0} value={form.prep_time_minutes} onChange={(e) => set("prep_time_minutes", Number(e.target.value))} data-testid="form-prep" className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className={label}>Cook (min)</label>
                <input type="number" min={0} value={form.cook_time_minutes} onChange={(e) => set("cook_time_minutes", Number(e.target.value))} data-testid="form-cook" className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className={label}>Total (min)</label>
                <input type="number" min={0} value={form.total_time_minutes} onChange={(e) => set("total_time_minutes", Number(e.target.value))} data-testid="form-total" className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className={label}>Servings</label>
                <input type="number" min={1} value={form.servings} onChange={(e) => set("servings", Number(e.target.value))} data-testid="form-servings" className={inputCls} style={inputStyle} />
              </div>
              <div className="col-span-2">
                <label className={label}>Calories</label>
                <input type="number" min={0} value={form.calories} onChange={(e) => set("calories", Number(e.target.value))} data-testid="form-calories" className={inputCls} style={inputStyle} />
              </div>
            </div>
          </section>

          {/* Flags */}
          <section className="rounded-xl p-6 space-y-3" style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}>
            <h2 className="font-serif text-xl mb-2">Visibility</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" className="w-5 h-5 accent-[color:var(--jaf-primary)]" />
              <span className="text-sm">Show on homepage "Featured"</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.new_this_week} onChange={(e) => set("new_this_week", e.target.checked)} data-testid="form-new" className="w-5 h-5 accent-[color:var(--jaf-primary)]" />
              <span className="text-sm">Show in "New this week"</span>
            </label>
          </section>
        </aside>
      </form>
    </div>
  );
}
